<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Courses</h1>
        <div>
          <button class="btn btn-outline-secondary mr-2" @click="showUploadModal = true">
            <b-icon icon="cloud-upload" class="mr-1"></b-icon> Upload Excel
          </button>
          <button class="btn btn-primary" @click="openCreateModal">
            <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Course
          </button>
        </div>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-4">
              <b-form-input
                v-model="filter"
                placeholder="Search by code or description..."
                debounce="300"
              ></b-form-input>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="unitFilter" :options="unitOptions" @change="currentPage = 1"></b-form-select>
            </div>
            <div class="col-md-2 ml-auto text-right">
              <small class="text-muted" v-if="!loading">{{ filteredCourses.length }} course(s)</small>
            </div>
          </div>

          <b-table
            :items="filteredCourses"
            :fields="fields"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading courses...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No courses found.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(unit)="data">
              <b-badge variant="info">{{ data.value }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="filteredCourses.length > perPage">
            <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(filteredCourses.length / perPage) }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredCourses.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Course' : 'Add Course'"
        @ok.prevent="saveCourse"
        :ok-disabled="saving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveCourse">
          <b-form-group label="Course Code" label-for="course-code">
            <b-form-input
              id="course-code"
              v-model="form.code"
              placeholder="e.g. CMP 501"
              required
              :disabled="saving"
              :state="formErrors.code ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.code }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Description" label-for="course-desc">
            <b-form-input
              id="course-desc"
              v-model="form.description"
              placeholder="e.g. Introduction to Computing"
              required
              :disabled="saving"
              :state="formErrors.description ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.description }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Credit Unit" label-for="course-unit">
            <b-form-input
              id="course-unit"
              v-model="form.unit"
              type="number"
              min="0"
              max="30"
              placeholder="e.g. 3"
              required
              :disabled="saving"
              :state="formErrors.unit ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.unit }}</b-form-invalid-feedback>
          </b-form-group>
        </b-form>
      </b-modal>

      <!-- Upload Excel Modal -->
      <b-modal
        v-model="showUploadModal"
        title="Upload Courses (Excel)"
        @ok.prevent="uploadExcel"
        :ok-disabled="uploading || !uploadFile"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="uploading" class="mr-1"></b-spinner>
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </template>
        <p class="text-muted small mb-3">
          Excel file should have columns: <strong>Code</strong>, <strong>Description</strong>, <strong>Unit</strong> (in that order, first row as header).
        </p>
        <b-form-group label="Select File">
          <b-form-file
            v-model="uploadFile"
            accept=".xlsx,.xls,.csv"
            placeholder="Choose a file..."
            :disabled="uploading"
          ></b-form-file>
        </b-form-group>
        <b-progress
          v-if="uploadProgress > 0"
          :value="uploadProgress"
          max="100"
          show-progress
          animated
          :variant="uploadProgress === 100 ? 'success' : 'info'"
          class="mt-2"
        ></b-progress>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseManagement',
  data() {
    return {
      courses: [],
      loading: false,
      filter: '',
      unitFilter: null,
      sortBy: 'code',
      sortDesc: false,
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: 'index', label: '#', sortable: false },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'description', label: 'Description', sortable: true },
        { key: 'unit', label: 'Unit', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      showModal: false,
      isEditing: false,
      editingId: null,
      saving: false,
      form: { code: '', description: '', unit: '' },
      formErrors: {},
      deleting: false,
      deletingId: null,
      showUploadModal: false,
      uploading: false,
      uploadFile: null,
      uploadProgress: 0,
    };
  },
  computed: {
    unitOptions() {
      const units = [...new Set(this.courses.map(c => c.unit))].filter(u => u != null).sort((a, b) => a - b);
      return [{ value: null, text: 'All Units' }, ...units.map(u => ({ value: u, text: u + ' unit(s)' }))];
    },
    filteredCourses() {
      let list = this.courses;
      if (this.filter) {
        const s = this.filter.toLowerCase();
        list = list.filter(c => (c.code && c.code.toLowerCase().includes(s)) || (c.description && c.description.toLowerCase().includes(s)));
      }
      if (this.unitFilter != null) {
        list = list.filter(c => c.unit == this.unitFilter);
      }
      return list;
    },
  },
  created() {
    this.loadCourses();
  },
  methods: {
    loadCourses() {
      this.loading = true;
      axios.get('/admin/courses')
        .then(res => {
          this.courses = res.data.courses || [];
        })
        .catch(() => {
          Toast.fire({ icon: 'error', title: 'Failed to load courses' });
        })
        .finally(() => { this.loading = false; });
    },
    openCreateModal() {
      this.isEditing = false;
      this.editingId = null;
      this.form = { code: '', description: '', unit: '' };
      this.formErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.form = { code: item.code, description: item.description, unit: item.unit };
      this.formErrors = {};
      this.showModal = true;
    },
    saveCourse() {
      this.formErrors = {};
      if (!this.form.code || this.form.code.trim().length < 7) {
        this.formErrors.code = 'Course code is required (min 7 characters)';
        return;
      }
      if (!this.form.description || !this.form.description.trim()) {
        this.formErrors.description = 'Description is required';
        return;
      }
      if (this.form.unit === '' || this.form.unit === null || isNaN(this.form.unit)) {
        this.formErrors.unit = 'Credit unit is required';
        return;
      }

      this.saving = true;
      const payload = {
        code: this.form.code.trim(),
        description: this.form.description.trim(),
        unit: parseInt(this.form.unit),
      };

      const request = this.isEditing
        ? axios.put('/admin/courses/' + encodeURIComponent(this.editingId), payload)
        : axios.post('/admin/courses', payload);

      request
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Saved' });
          this.showModal = false;
          this.loadCourses();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to save course' });
        })
        .finally(() => { this.saving = false; });
    },
    confirmDelete(item) {
      Swal.fire({
        title: 'Delete Course?',
        html: `Are you sure you want to delete <strong>${item.code}</strong>?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete',
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteCourse(item);
        }
      });
    },
    deleteCourse(item) {
      this.deleting = true;
      this.deletingId = item.id;
      axios.delete('/admin/courses/' + encodeURIComponent(item.id))
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Course deleted' });
          this.loadCourses();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to delete course' });
        })
        .finally(() => { this.deleting = false; this.deletingId = null; });
    },
    uploadExcel() {
      if (!this.uploadFile) return;
      this.uploading = true;
      this.uploadProgress = 0;

      let formData = new FormData();
      formData.append('courses', this.uploadFile);

      axios.post('/admin/upload/courses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          this.uploadProgress = parseInt(Math.round((e.loaded / e.total) * 100));
        },
      })
        .then(() => {
          Toast.fire({ icon: 'success', title: 'Courses uploaded successfully' });
          this.showUploadModal = false;
          this.uploadFile = null;
          this.uploadProgress = 0;
          this.loadCourses();
        })
        .catch(() => {
          Toast.fire({ icon: 'error', title: 'Error uploading courses' });
        })
        .finally(() => { this.uploading = false; });
    },
  },
};
</script>
